import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";

import { Skeleton } from "components";
import { useLoader } from "hooks";
import { uiActions } from "store";
import { getCurrentUserAccessToken } from "store/user/user.reducer";
import { UWCheckResultMode } from "types";

import CheckListActions from "./checkListActions";
import CheckListStatus from "./checkListStatus";

import styles from "./checkListRow.module.scss";

export type CheckResult =
	| {
			success?: boolean;
			auroraOutcome?: string | null;
			resultMode?: string;
			isFraud?: boolean;
			bankruptcyStatus?: string;
			errorMessage?: string;
	  }
	| undefined;

type CheckListRowProps = {
	checkboxLabel: string;
	checkboxName: string;
	isHiddenCheckResult?: boolean;
	checkResult?: CheckResult;
	isLoading: boolean;
	ownerType?: string;
	downloadDocument?: (id: string | any, ownerType?: number | any) => void;
	isLoadingDownload?: boolean;
	isLoadingAuroraOutcome?: boolean;
	isDisabledOutcome?: boolean;
	isDisabledActions?: boolean;
	isDisabledViewBtn?: boolean;
	isStatusNotApplicable?: boolean;
	applicationId: any;
	submitAuroraOutcome?: (
		id?: any,
		ownerType?: any,
		values?: any,
		onSuccessHandler?: Function,
	) => void;
	refetchData?: Function;
	handleRerun?: Function;
	onFraudClickFn?: any;
	tooltip?: string;
	data?: any;
};

export const CheckListRow = ({
	checkboxLabel,
	checkboxName,
	isHiddenCheckResult,
	checkResult,
	isLoading,
	ownerType,
	downloadDocument,
	isLoadingDownload,
	isLoadingAuroraOutcome,
	isDisabledOutcome,
	isDisabledActions,
	isDisabledViewBtn,
	applicationId,
	submitAuroraOutcome,
	refetchData,
	handleRerun,
	onFraudClickFn,
	tooltip,
	data,
	isStatusNotApplicable,
}: CheckListRowProps) => {
	const history = useHistory();
	const dispatch = useDispatch();

	const token = useSelector(getCurrentUserAccessToken);

	const { params, url } = useRouteMatch<any>();
	const isMerchantPage = useMemo(() => url?.startsWith("/merchant/"), [url]);

	const getUnderwritingUrl = (ownerType, checkboxName) => {
		return `/underwriting/${params.tab}/${params.id}/${
			params.underwritingTab
		}${ownerType ? `/${ownerType}` : ""}/${checkboxName}`;
	};

	const getMerchantUrl = (ownerType, checkboxName) => {
		return `/merchant/list/info/${params.id}/results/${applicationId}${
			ownerType ? `/${ownerType}` : ""
		}/${checkboxName}`;
	};

	const onClickViewResult = () => {
		if (checkboxName === "alloy") {
			dispatch(
				uiActions.showModal("UW_RESULT", {
					title: "Alloy Results",
					data,
				}),
			);
		} else {
			const underwritingUrl = getUnderwritingUrl(ownerType, checkboxName);
			const merchantUrl = getMerchantUrl(ownerType, checkboxName);

			history.push(isMerchantPage ? merchantUrl : underwritingUrl);
		}
	};

	const onClickDownload = () => {
		if (downloadDocument) {
			downloadDocument({
				id: isMerchantPage ? applicationId : params?.id,
				ownerType: ownerType,
				token: token,
			});
		}
	};

	const onClickRerun = () => handleRerun?.();

	const auroraOutcomeSubmitHandler = useCallback(
		values =>
			submitAuroraOutcome?.({
				id: isMerchantPage ? applicationId : params?.id,
				values,
				ownerType: ownerType,
				onSuccessHandler: refetchData,
			}),
		[
			params?.id,
			applicationId,
			isMerchantPage,
			ownerType,
			refetchData,
			submitAuroraOutcome,
		],
	);

	const Loader = useLoader(isLoading);

	const isExtractDataFailed =
		checkResult?.resultMode === UWCheckResultMode.Empty.name ||
		checkResult?.resultMode === UWCheckResultMode.Error.name;

	return (
		<Loader
			skeleton={
				<Skeleton
					variant="text"
					height="30px"
					width="90%"
					margin="0px 30px 13px 30px"
				/>
			}
			component={
				<div className={styles.checkListRow}>
					<div className={styles.checkListCol}>
						<div className={styles.checkListColLabel}>
							{checkboxLabel}
							{checkResult?.isFraud && (
								<p
									className={`${styles.alertFraud} ${
										onFraudClickFn && styles.pointer
									}`}
									onClick={() => onFraudClickFn?.()}
								>
									Fraud
								</p>
							)}
							{checkResult?.bankruptcyStatus && (
								<p className={styles.alertBankruptcy}>
									Bankruptcy
								</p>
							)}
						</div>
					</div>
					<div className={styles.checkListCol}>
						<div className={styles.checkListColActions}>
							<div className={styles.checkListStatusHolder}>
								{!isHiddenCheckResult &&
									(isStatusNotApplicable ? (
										<div className={styles.notApplicable}>
											Not Applicable
										</div>
									) : (
										<CheckListStatus
											checkResult={checkResult}
											isLoading={isLoadingAuroraOutcome}
											handleAuroraOutcomeSubmit={
												auroraOutcomeSubmitHandler
											}
											showAuroraOutcome={
												!!submitAuroraOutcome &&
												checkResult?.success !==
													undefined &&
												!isExtractDataFailed
											}
											isExtractDataFailed={
												isExtractDataFailed
											}
											isDisabledOutcome={
												isDisabledOutcome
											}
											tooltip={tooltip}
										/>
									))}
							</div>

							<CheckListActions
								handleDownload={
									downloadDocument && onClickDownload
								}
								handleViewResult={onClickViewResult}
								handleRerun={onClickRerun}
								isLoading={isLoading}
								isDownloading={isLoadingDownload}
								showRerun={isExtractDataFailed}
								isDisabled={isDisabledActions}
								isDisabledViewBtn={isDisabledViewBtn}
								btnTooltip={tooltip}
							/>
						</div>
					</div>
				</div>
			}
		/>
	);
};
